import type { PrimitiveAtom } from 'jotai';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import type { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';
import { rawBoolStorage, rawNumberStorage, rawStringStorage, syncJsonStorage } from '@/atoms/storage';
import { jotaiStore } from '@/atoms/store';

export type LegacyCodec = 'raw-bool' | 'raw-num' | 'raw-str';

export interface PrefOpts<T> {
    /** Opt out of the JSON codec for prefs that were stored as raw strings before migration. */
    legacy?: LegacyCodec;
    /** Legacy localStorage key to migrate from on first run. */
    from?: string;
    /** Runs after decode; throws on shape mismatch → caller logs and falls back to the default. */
    validate?: (raw: unknown) => T;
}

export interface PrefBuilder<T> {
    readonly defaultValue: T;
    readonly opts: PrefOpts<T>;
    readonly __pref: true;
}

export function pref<T>(defaultValue: T, opts: PrefOpts<T> = {}): PrefBuilder<T> {
    return { defaultValue, opts, __pref: true };
}

function chooseCodec<T>(defaultValue: T, opts: PrefOpts<T>): SyncStorage<T> {
    if (!opts.legacy) return syncJsonStorage<T>();
    // biome-ignore lint/suspicious/noExplicitAny: legacy codecs project T at runtime
    const adapters: Record<LegacyCodec, SyncStorage<any>> = {
        'raw-bool': rawBoolStorage(defaultValue as unknown as boolean),
        'raw-num': rawNumberStorage,
        'raw-str': rawStringStorage,
    };
    return adapters[opts.legacy] as SyncStorage<T>;
}

function wrapValidate<T>(inner: SyncStorage<T>, validate: (raw: unknown) => T): SyncStorage<T> {
    return {
        ...inner,
        getItem: (key, initialValue) => {
            const raw = inner.getItem(key, initialValue);
            try {
                return validate(raw);
            } catch (err) {
                // biome-ignore lint/suspicious/noConsole: intentional warning when a stored pref fails validation
                console.warn(`[prefs] validation failed for ${key}; using default`, err);
                inner.removeItem(key);
                return initialValue;
            }
        },
    };
}

export class Pref<T> {
    readonly key: string;
    readonly defaultValue: T;
    readonly opts: PrefOpts<T>;
    readonly storageKey: string;
    private cachedAtom?: PrimitiveAtom<T>;

    constructor(key: string, builder: PrefBuilder<T>) {
        this.key = key;
        this.defaultValue = builder.defaultValue;
        this.opts = builder.opts;
        this.storageKey = `pref:${key}`;
    }

    get atom(): PrimitiveAtom<T> {
        if (!this.cachedAtom) {
            const base = chooseCodec(this.defaultValue, this.opts);
            const codec = this.opts.validate ? wrapValidate(base, this.opts.validate) : base;
            this.cachedAtom = atomWithStorage(this.storageKey, this.defaultValue, codec, { getOnInit: true });
        }
        return this.cachedAtom;
    }

    use(): T {
        // biome-ignore lint/correctness/useHookAtTopLevel: class-bound hook method; callers invoke from a React component
        return useAtomValue(this.atom);
    }
    useSet(): (v: T) => void {
        return useSetAtom(this.atom);
    }
    useState(): readonly [T, (v: T) => void] {
        return useAtom(this.atom);
    }
    get(): T {
        return jotaiStore.get(this.atom);
    }
    set(value: T): void {
        jotaiStore.set(this.atom, value);
    }

    /** Test-only: drop the cached atom so the next access re-reads from the current backend.
     *  `getOnInit` reads storage once, when the atom is created — not per store —
     *  so a "survives reload" test needs this plus a fresh store, and must reset
     *  again afterwards or the next test inherits the value. */
    __reset(): void {
        this.cachedAtom = undefined;
    }
}

export type PrefMap<R extends Record<string, PrefBuilder<unknown>>> = {
    [K in keyof R]: R[K] extends PrefBuilder<infer T> ? Pref<T> : never;
};

export type PrefSchema<R extends Record<string, PrefBuilder<unknown>>> = {
    [K in keyof R]: R[K] extends PrefBuilder<infer T> ? T : never;
};

export function definePrefs<R extends Record<string, PrefBuilder<unknown>>>(defs: R): PrefMap<R> {
    const out: Record<string, Pref<unknown>> = {};
    for (const [key, builder] of Object.entries(defs)) {
        out[key] = new Pref(key, builder);
    }
    return out as PrefMap<R>;
}

/** Test-only: drop cached atoms across the registry. */
export function __resetPrefMap(map: Record<string, { __reset(): void }>): void {
    for (const entry of Object.values(map)) {
        entry.__reset();
    }
}
